import dayjs from "dayjs";
import { useMemo } from "react";
import { Badge } from "react-bootstrap";
import { ChevronDown } from "react-bootstrap-icons";
import {
  type Client,
  CommunicationStatus,
  type ViewCommunication,
} from "@/briefpoint-client";
import { SHOW_REMINDER_EMAIL_STATUSES } from "@/lib/constants";
import type { DocumentClient } from "@/lib/types";
import Button from "../Button";

type Props = {
  communication?: ViewCommunication;
  document?: DocumentClient;
  client?: Client;
  setReviewExpanded: (expanded: boolean) => void;
  reviewExpanded: boolean;
  setShowEmailModal: (show: boolean) => void;
  showEmailModal: boolean;
  setSelectedClient: (client: Client | undefined) => void;
  selectedClient?: Client | undefined;
};

const SHOW_REMINDER_AFTER_DAYS = 5;

export default function DocumentListTableRowCommunication(props: Props) {
  const canSendReminderEmail =
    (props.communication?.status &&
      SHOW_REMINDER_EMAIL_STATUSES.includes(props.communication.status)) ??
    false;

  const timePassed = dayjs().diff(
    dayjs(props.communication?.lastActivityDate),
    "days",
  );

  const isNewCommunication = useMemo(() => {
    if (
      !props.communication?.lastActivityDate ||
      props.communication?.status !== CommunicationStatus.Complete
    )
      return false;
    if (
      !props.document?.info?.lastViewDate ||
      props.communication?.lastActivityDate < props.document?.info?.lastViewDate
    )
      return true;
    return false;
  }, [props.communication, props.document]);

  function getReviewText(communication: ViewCommunication | undefined): string {
    if (!communication) {
      return "";
    }

    const formatDate = (date: Date | null | undefined): string => {
      if (!date) return "";
      return dayjs(date).format("MM/DD");
    };

    switch (communication.status) {
      case CommunicationStatus.Sent:
        return `Review Requested ${formatDate(communication.lastActivityDate)}`;
      case CommunicationStatus.Open:
        return `Document Opened ${formatDate(communication.lastActivityDate)}`;
      case CommunicationStatus.InProgress:
        return `New Response ${formatDate(communication.lastActivityDate)}`;
      case CommunicationStatus.ChangesRequested:
        return `Changes Requested on ${formatDate(communication.lastActivityDate)}`;
      case CommunicationStatus.Complete:
        return `Review Completed ${formatDate(communication.lastActivityDate)}`;
      case CommunicationStatus.Pending:
        return `Draft Started ${formatDate(communication.lastActivityDate ?? communication.createdDate)}`;
      default:
        return "";
    }
  }
  return (
    <div>
      <div className="d-flex gap-1 align-items-center">
        {isNewCommunication && <Badge bg="success">New</Badge>}
        <div className="me-1">{getReviewText(props.communication)}</div>

        {!!props.communication &&
          props.communication.status !== CommunicationStatus.Closed && (
            <ChevronDown
              onClick={() => props.setReviewExpanded(!props.reviewExpanded)}
            />
          )}
      </div>

      {timePassed > SHOW_REMINDER_AFTER_DAYS && canSendReminderEmail && (
        <div>
          Last activity {timePassed} days ago.{" "}
          <Button
            className="small p-0"
            size="sm"
            variant="link"
            onClick={() => {
              props.setShowEmailModal(true);
              props.setSelectedClient(props.client);
            }}
          >
            Send client a reminder?
          </Button>
        </div>
      )}
    </div>
  );
}
